import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Badge,
  Button,
  Card,
  Flex,
  Skeleton,
  Table,
  Tag,
  Typography,
  message,
  DownloadOutlined,
  FileTextOutlined,
  UploadOutlined,
} from '@/shared/antd-imports';
import { CheckSquareOutlined, LinkOutlined } from '@ant-design/icons';
import { useGetDocumentDownloadMutation, useGetProjectDocumentsQuery } from '@/store/api';
import type { ClientProjectDocument } from '@/types/project-documents';
import { useIsPhone } from '@/hooks/useIsPhone';
import { MobileCardList } from '@/components/common/MobileCardList';
import { formatCalendarDate } from '@/utils/money';
import { DocumentDecisionModal } from './DocumentDecisionModal';
import { DocumentVersionsModal } from './DocumentVersionsModal';
import { DocumentUploadModal } from './DocumentUploadModal';
import { approvalTagColor, documentCategoryLabel, documentFileLabel, openDownload, visuallyHidden } from './document-utils';

interface ProjectDocumentsCardProps {
  projectId: string;
}

/**
 * DOC-5 + DOC-3 portal half (ANS-56): the documents the provider shared on this project. The client opens or downloads
 * them, and approves or rejects a version the provider sent for approval (name, time and IP are recorded). Cards on
 * phones, a table elsewhere. Loading, error (retry) and empty states.
 */
export const ProjectDocumentsCard: React.FC<ProjectDocumentsCardProps> = ({ projectId }) => {
  const { t, i18n } = useTranslation();
  const isPhone = useIsPhone();
  const { data, isLoading, isError, isFetching, refetch } = useGetProjectDocumentsQuery(projectId);
  const [download, { isLoading: isDownloading }] = useGetDocumentDownloadMutation();
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [reviewing, setReviewing] = useState<ClientProjectDocument | null>(null);
  const [browsing, setBrowsing] = useState<ClientProjectDocument | null>(null);
  const [isUploadOpen, setIsUploadOpen] = useState(false);

  const documents = data?.body?.documents ?? [];
  const awaiting = data?.body?.awaiting_approval ?? 0;

  const title = (
    <Flex gap={8} align="center" wrap="wrap">
      <FileTextOutlined aria-hidden />
      {t('documents.title', { defaultValue: 'Documents' })}
      {awaiting > 0 && (
        <Badge
          count={awaiting}
          title={t('documents.awaitingCount', {
            defaultValue: '{{count}} waiting for your approval',
            count: awaiting,
          })}
        />
      )}
    </Flex>
  );

  const openDocument = async (doc: ClientProjectDocument) => {
    if (!doc.version_id) return;
    setDownloadingId(doc.id);
    try {
      const result = await download({ projectId, documentId: doc.id, versionId: doc.version_id }).unwrap();
      if (!(result.done && result.body && openDownload(result.body))) message.error(t('documents.downloadError', { defaultValue: 'The file could not be opened. Please try again.' }));
    } catch {
      message.error(t('documents.downloadError', { defaultValue: 'The file could not be opened. Please try again.' }));
    } finally {
      setDownloadingId(null);
    }
  };

  const approvalTag = (doc: ClientProjectDocument) => {
    if (doc.uploaded_by_client) {
      // DOC-5 second half (stream mlo): a file the client sent from here.
      return <Tag color="blue" style={{ marginInlineEnd: 0 }}>{t('documents.sentByYou', { defaultValue: 'Sent by you' })}</Tag>;
    }
    if (doc.awaiting_my_approval) {
      return <Tag color="gold" style={{ marginInlineEnd: 0 }}>{t('documents.awaitingYou', { defaultValue: 'Waiting for your approval' })}</Tag>;
    }
    if (doc.approval_status === 'approved' || doc.approval_status === 'rejected') {
      return (
        <Tag color={approvalTagColor(doc.approval_status)} style={{ marginInlineEnd: 0 }}>
          {doc.approval_status === 'approved'
            ? t('documents.approved', { defaultValue: 'Approved' })
            : t('documents.rejected', { defaultValue: 'Rejected' })}
        </Tag>
      );
    }
    return null;
  };

  const actions = (doc: ClientProjectDocument) => (
    <>
      <Button
        size="small"
        icon={doc.is_link ? <LinkOutlined /> : <DownloadOutlined />}
        loading={isDownloading && downloadingId === doc.id}
        disabled={!doc.available || !doc.version_id}
        onClick={() => void openDocument(doc)}
        aria-label={
          doc.is_link
            ? t('documents.openNamed', { defaultValue: 'Open {{title}}', title: doc.title })
            : t('documents.downloadNamed', { defaultValue: 'Download {{title}}', title: doc.title })
        }
      >
        {doc.is_link ? t('documents.open', { defaultValue: 'Open' }) : t('documents.download', { defaultValue: 'Download' })}
      </Button>
      {(doc.earlier_versions ?? 0) > 0 && (
        <Button size="small" type="link" onClick={() => setBrowsing(doc)}>
          {t('documents.earlierVersions', {
            defaultValue: 'Earlier versions ({{count}})',
            count: doc.earlier_versions,
          })}
        </Button>
      )}
      {doc.awaiting_my_approval && (
        <Button size="small" type="primary" icon={<CheckSquareOutlined />} onClick={() => setReviewing(doc)}>
          {t('documents.review', { defaultValue: 'Review and approve' })}
        </Button>
      )}
    </>
  );

  const meta = (doc: ClientProjectDocument) => (
    <>
      <div>
        {documentCategoryLabel(t, doc.category)}
        {doc.version_no && doc.version_no > 1
          ? ` · ${t('documents.version', { defaultValue: 'Version {{no}}', no: doc.version_no })}`
          : ''}
      </div>
      <div>{documentFileLabel(t, doc)}</div>
      <div>
        {t('documents.updated', {
          defaultValue: 'Updated {{date}}',
          date: formatCalendarDate(doc.updated_at.slice(0, 10), i18n.language),
        })}
      </div>
    </>
  );

  let body: React.ReactNode;
  if (isLoading) {
    body = <Skeleton active paragraph={{ rows: 2 }} title={false} />;
  } else if (isError) {
    body = (
      <Alert
        type="error"
        showIcon
        message={t('documents.loadError', { defaultValue: 'The documents could not be loaded.' })}
        action={
          <Button size="small" onClick={() => refetch()} loading={isFetching}>
            {t('documents.retry', { defaultValue: 'Try again' })}
          </Button>
        }
      />
    );
  } else if (isPhone || documents.length === 0) {
    body = (
      <MobileCardList<ClientProjectDocument>
        items={documents}
        rowKey={doc => doc.id}
        ariaLabel={t('documents.title', { defaultValue: 'Documents' })}
        emptyText={t('documents.empty', {
          defaultValue: 'No documents shared yet. Documents your provider shares with you appear here.',
        })}
        renderTitle={doc => doc.title}
        renderExtra={approvalTag}
        renderMeta={meta}
        renderActions={actions}
      />
    );
  } else {
    body = (
      <Table<ClientProjectDocument>
        size="small"
        rowKey="id"
        dataSource={documents}
        pagination={documents.length > 10 ? { pageSize: 10, size: 'small' } : false}
        scroll={{ x: 'max-content' }}
        columns={[
          {
            key: 'title',
            title: t('documents.document', { defaultValue: 'Document' }),
            render: (_: unknown, doc) => (
              <Flex vertical gap={2} style={{ minWidth: 200, maxWidth: 420 }}>
                <Typography.Text strong>{doc.title}</Typography.Text>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {documentFileLabel(t, doc)}
                  {doc.version_no && doc.version_no > 1
                    ? ` · ${t('documents.version', { defaultValue: 'Version {{no}}', no: doc.version_no })}`
                    : ''}
                </Typography.Text>
                {doc.description && (
                  <Typography.Text type="secondary" style={{ fontSize: 12 }} ellipsis={{ tooltip: doc.description }}>
                    {doc.description}
                  </Typography.Text>
                )}
              </Flex>
            ),
          },
          {
            key: 'category',
            title: t('documents.category', { defaultValue: 'Type' }),
            render: (_: unknown, doc) => <Tag>{documentCategoryLabel(t, doc.category)}</Tag>,
          },
          {
            key: 'updated',
            title: t('documents.updatedColumn', { defaultValue: 'Updated' }),
            render: (_: unknown, doc) => formatCalendarDate(doc.updated_at.slice(0, 10), i18n.language),
          },
          {
            key: 'approval',
            title: t('documents.approval', { defaultValue: 'Approval' }),
            render: (_: unknown, doc) => approvalTag(doc) ?? <Typography.Text type="secondary">-</Typography.Text>,
          },
          {
            key: 'actions',
            title: <span style={visuallyHidden}>{t('documents.actions', { defaultValue: 'Actions' })}</span>,
            render: (_: unknown, doc) => <Flex gap={8} wrap="wrap">{actions(doc)}</Flex>,
          },
        ]}
      />
    );
  }

  return (
    <Card
      title={title}
      style={{ marginBottom: 16 }}
      extra={
        <Button size="small" icon={<UploadOutlined />} onClick={() => setIsUploadOpen(true)}>
          {t('documents.upload.open', { defaultValue: 'Upload a file' })}
        </Button>
      }
    >
      {body}
      {reviewing && (
        <DocumentDecisionModal
          projectId={projectId}
          document={reviewing}
          onOpenDocument={() => void openDocument(reviewing)}
          onClose={() => setReviewing(null)}
        />
      )}
      {isUploadOpen && <DocumentUploadModal projectId={projectId} onClose={() => setIsUploadOpen(false)} />}
      {browsing && <DocumentVersionsModal projectId={projectId} document={browsing} onClose={() => setBrowsing(null)} />}
    </Card>
  );
};

export default ProjectDocumentsCard;
